// Particle effects: muzzle blasts, shell splashes, fires, smoke, spray.
// One pooled point cloud with a custom shader (per-particle size/color/fade),
// spawned into either world space or ship space (two instances).

import * as THREE from "three";

const MAX = 4000;

export class Particles {
  constructor(parent) {
    this.n = 0;
    this.pos = new Float32Array(MAX * 3);
    this.vel = new Float32Array(MAX * 3);
    this.col = new Float32Array(MAX * 3);
    this.life = new Float32Array(MAX);   // remaining
    this.life0 = new Float32Array(MAX);  // initial
    this.size = new Float32Array(MAX);
    this.grav = new Float32Array(MAX);
    this.drag = new Float32Array(MAX);
    this.grow = new Float32Array(MAX);

    const geo = new THREE.BufferGeometry();
    this.aPos = new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage);
    this.aSize = new THREE.BufferAttribute(new Float32Array(MAX), 1).setUsage(THREE.DynamicDrawUsage);
    this.aAlpha = new THREE.BufferAttribute(new Float32Array(MAX), 1).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("position", this.aPos);
    geo.setAttribute("pcolor", this.aCol);
    geo.setAttribute("psize", this.aSize);
    geo.setAttribute("palpha", this.aAlpha);
    geo.setDrawRange(0, 0);

    const mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      vertexShader: `
        attribute vec3 pcolor; attribute float psize; attribute float palpha;
        varying vec3 vC; varying float vA;
        void main() {
          vC = pcolor; vA = palpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = psize * (900.0 / max(1.0, -mv.z));
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        varying vec3 vC; varying float vA;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float r = length(d);
          if (r > 0.5) discard;
          float soft = smoothstep(0.5, 0.18, r);
          gl_FragColor = vec4(vC, vA * soft);
        }`,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
    parent.add(this.points);
  }

  spawn(at, opts = {}) {
    const count = opts.count ?? 6;
    const colors = opts.colors ?? [0xffffff];
    for (let i = 0; i < count; i++) {
      if (this.n >= MAX) return;
      const j = this.n++;
      const sp = opts.speed ?? 3;
      this.pos[j * 3] = at.x + (Math.random() - 0.5) * (opts.jitter ?? 0.6);
      this.pos[j * 3 + 1] = at.y + (Math.random() - 0.5) * (opts.jitter ?? 0.6);
      this.pos[j * 3 + 2] = at.z + (Math.random() - 0.5) * (opts.jitter ?? 0.6);
      let vx = (Math.random() - 0.5), vy = (Math.random() - 0.5), vz = (Math.random() - 0.5);
      if (opts.dir) { vx = vx * 0.5 + opts.dir.x; vy = vy * 0.5 + opts.dir.y; vz = vz * 0.5 + opts.dir.z; }
      const m = sp * (0.4 + Math.random() * 0.9) / Math.hypot(vx, vy, vz);
      this.vel[j * 3] = vx * m; this.vel[j * 3 + 1] = vy * m; this.vel[j * 3 + 2] = vz * m;
      const c = new THREE.Color(colors[(Math.random() * colors.length) | 0]);
      this.col[j * 3] = c.r; this.col[j * 3 + 1] = c.g; this.col[j * 3 + 2] = c.b;
      this.life[j] = this.life0[j] = (opts.life ?? 1.5) * (0.6 + Math.random() * 0.7);
      this.size[j] = (opts.size ?? 1) * (0.7 + Math.random() * 0.6);
      this.grav[j] = opts.gravity ?? 0;
      this.drag[j] = opts.drag ?? 0.2;
      this.grow[j] = opts.grow ?? 0;
    }
  }

  update(dt) {
    let n = this.n;
    for (let j = 0; j < n;) {
      this.life[j] -= dt;
      if (this.life[j] <= 0) {
        // swap-remove
        n--;
        for (const arr of [this.pos, this.vel, this.col])
          { arr[j*3] = arr[n*3]; arr[j*3+1] = arr[n*3+1]; arr[j*3+2] = arr[n*3+2]; }
        this.life[j] = this.life[n]; this.life0[j] = this.life0[n];
        this.size[j] = this.size[n]; this.grav[j] = this.grav[n];
        this.drag[j] = this.drag[n]; this.grow[j] = this.grow[n];
        continue;
      }
      const d = Math.max(0, 1 - this.drag[j] * dt);
      this.vel[j*3] *= d; this.vel[j*3+2] *= d;
      this.vel[j*3+1] = this.vel[j*3+1] * d + this.grav[j] * dt;
      this.pos[j*3] += this.vel[j*3] * dt;
      this.pos[j*3+1] += this.vel[j*3+1] * dt;
      this.pos[j*3+2] += this.vel[j*3+2] * dt;
      j++;
    }
    this.n = n;
    // push attribute updates
    const sizeA = this.aSize.array, alphaA = this.aAlpha.array;
    for (let j = 0; j < n; j++) {
      const t = this.life[j] / this.life0[j];
      sizeA[j] = this.size[j] * (1 + this.grow[j] * (1 - t));
      alphaA[j] = Math.min(1, t * 2.2);
    }
    this.aPos.needsUpdate = true; this.aCol.needsUpdate = true;
    this.aSize.needsUpdate = true; this.aAlpha.needsUpdate = true;
    this.points.geometry.setDrawRange(0, n);
  }
}

// ---------------------------------------------------------------------------
// canned effects

export function muzzleBlast(p, at, dir) {
  p.spawn(at, { count: 26, dir, speed: 30, life: 0.35, size: 7, jitter: 2,
    colors: [0xffd890, 0xffb050, 0xfff4c8], drag: 3.5, grow: 3 });
  p.spawn(at, { count: 40, dir, speed: 14, life: 3.2, size: 6, jitter: 3,
    colors: [0x8a7d66, 0xa39684, 0x6e6353], drag: 1.4, gravity: 0.6, grow: 2.5 });
}

export function shellSplash(p, at, big = true) {
  const h = big ? 22 : 9; // 16" splashes tower ~20 m
  p.spawn(at, { count: big ? 46 : 16, dir: new THREE.Vector3(0, 1, 0), speed: h, life: 2.6,
    size: big ? 5 : 2.5, jitter: 4, colors: [0xeef4f8, 0xcfe0ea, 0xb8ccd8], gravity: -9, drag: 0.35 });
  p.spawn(at, { count: 18, speed: 6, life: 1.4, size: 3.5, jitter: 6,
    colors: [0xdde9f0, 0xc4d6e2], gravity: -3, drag: 0.6, grow: 1.5 });
}

// A 16-inch HC round bursting ashore: flash, a climbing column of earth,
// and a slow, rolling brown-black cloud that hangs over the target.
export function groundBurst(p, at, big = true) {
  p.spawn(at, { count: big ? 22 : 10, speed: 26, life: 0.4, size: big ? 7 : 4, jitter: 3,
    colors: [0xffd890, 0xff9a3c, 0xfff0b0], drag: 2.2 });
  p.spawn(at, { count: big ? 42 : 14, dir: new THREE.Vector3(0, 1, 0), speed: big ? 30 : 14, life: 2.6,
    size: big ? 5.5 : 3, jitter: 7, colors: [0x6b5b40, 0x54473a, 0x3e372c], gravity: -9, drag: 0.45, grow: 1.5 });
  p.spawn(at, { count: big ? 24 : 8, speed: 7, life: 7, size: big ? 9 : 5, jitter: 10,
    colors: [0x54473a, 0x6e614f, 0x2c2c2c], gravity: 1.6, drag: 0.7, grow: 3 });
}

export function hitFlash(p, at) {
  p.spawn(at, { count: 30, speed: 22, life: 0.5, size: 5, jitter: 2,
    colors: [0xffcf5c, 0xff8a3c, 0xfff0b0], drag: 2.5 });
  p.spawn(at, { count: 26, speed: 8, life: 4, size: 5, jitter: 2,
    colors: [0x3a3a3a, 0x565049, 0x242424], gravity: 1.6, drag: 1.2, grow: 3 });
}

export function fireSmoke(p, at, intensity = 1) {
  p.spawn(at, { count: 2, speed: 2.2, life: 1.1, size: 1.6 * intensity, jitter: 0.8,
    colors: [0xff9a3c, 0xffc060, 0xff6a28], gravity: 2.5, drag: 1 });
  p.spawn(at, { count: 2, speed: 1.6, life: 3.6, size: 2.6 * intensity, jitter: 1,
    colors: [0x2c2c2c, 0x454039, 0x1c1c1c], gravity: 1.8, drag: 0.8, grow: 2.5 });
}

export function funnelSmoke(p, at, vel) {
  p.spawn(at, { count: 1, speed: 1.4, life: 4.5, size: 2.4, jitter: 1.4, dir: new THREE.Vector3(-vel.x * 0.12, 1, -vel.z * 0.12),
    colors: [0x6e7176, 0x7d8085, 0x5f6267], gravity: 1.1, drag: 0.35, grow: 3 });
}

export function bowSpray(p, at, side) {
  p.spawn(at, { count: 1, speed: 4, life: 1.2, size: 1.6, jitter: 1.2,
    dir: new THREE.Vector3(0.2, 0.7, side * 0.7),
    colors: [0xdfeaf2, 0xc9dae6], gravity: -6, drag: 0.4 });
}
